import React, { useCallback, useEffect, useRef, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstTable from "../../components/DstTable/DstTable";
import { useSession } from "../../contexts/SessionContext";
import { formatDateHumanly, formatDatePickerValue } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { DiplomaImportStructuredData, DiplomaImportValidatedData } from "./DiplomaImport.d";
import {
  createImportedDiplomas,
  handleDragLeave,
  handleDragOver,
  importCsvFile,
  onFileDrop,
  processFileInput,
  updateField,
} from "./DiplomaImport.function";
import "./DiplomaImport.scss";

export const DiplomaImport = () => {
  const { language, showAlert, popAlert } = useSession();
  const [csvData, setCsvData] = useState<Array<DiplomaImportStructuredData>>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [validatedData, setValidatedData] = useState<Array<DiplomaImportValidatedData>>([]);
  const [validating, setValidating] = useState<boolean>(false);
  const [editingBirthdateRowIndex, setEditingBirthdateRowIndex] = useState<number | null>(null);

  const _uploadCsvFile = useCallback(
    (file: File) => {
      if (showAlert && popAlert) {
        importCsvFile(file, language, setCsvData, setValidatedData, setValidating, showAlert, popAlert);
      }
    },
    [language, showAlert, popAlert]
  );

  useEffect(() => {
    const dropZone = dropZoneRef.current;
    if (dropZone) {
      dropZone.addEventListener("dragover", (event) => handleDragOver(event, dropZone));
      dropZone.addEventListener("dragleave", () => handleDragLeave(dropZone));
      dropZone.addEventListener("drop", (event) => onFileDrop(event, _uploadCsvFile));

      return () => {
        dropZone.removeEventListener("dragover", (event) => handleDragOver(event, dropZone));
        dropZone.removeEventListener("dragleave", () => handleDragLeave(dropZone));
        dropZone.removeEventListener("drop", (event) => onFileDrop(event, _uploadCsvFile));
      };
    }
  }, [_uploadCsvFile]);

  const columns = [
    {
      name: translate(language, "PAGES.DIPLOMA_IMPORT.TABLE_HEADER.CURSUS"),
      key: "cursus",
    },
    {
      name: translate(language, "PAGES.DIPLOMA_IMPORT.TABLE_HEADER.NAME"),
      key: "name",
    },
    {
      name: translate(language, "PAGES.DIPLOMA_IMPORT.TABLE_HEADER.BIRTHDATE"),
      key: "birthdate",
      render: (line: DiplomaImportValidatedData & DiplomaImportStructuredData) => (
        <div>
          {editingBirthdateRowIndex === line.rowIndex ? (
            <div className="cursor-pointer d-flex align-items-center">
              <Form.Control
                type="date"
                size="sm"
                value={line.birthdate || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  updateField(line.rowIndex, "birthdate", e.target.value, setCsvData, setValidatedData);
                }}
                onBlur={() => setEditingBirthdateRowIndex(null)}
                autoFocus
              />
            </div>
          ) : (
            <div
              className="cursor-pointer d-flex align-items-center"
              onClick={() => setEditingBirthdateRowIndex(line.rowIndex)}
            >
              <span>
                {formatDatePickerValue(line.birthdate) ||
                  translate(language, "PAGES.DIPLOMA_IMPORT.TABLE_HEADER.SELECT_BIRTHDATE")}
              </span>
              <span className="dst-icon-calendar ms-2" />
            </div>
          )}
        </div>
      ),
    },
    {
      name: translate(language, "PAGES.DIPLOMA_IMPORT.TABLE_HEADER.DATE"),
      key: "date",
      render: (line: DiplomaImportValidatedData & DiplomaImportStructuredData) => (
        <div>
          <span>{line.date || formatDateHumanly(language, new Date().toISOString())}</span>
        </div>
      ),
    },
    {
      name: translate(language, "PAGES.DIPLOMA_IMPORT.TABLE_HEADER.EMAIL"),
      key: "email",
    },
    {
      name: translate(language, "PAGES.DIPLOMA_IMPORT.TABLE_HEADER.IS_MINES"),
      key: "is_mines",
      render: (line: DiplomaImportStructuredData) => (
        <div className="form-check d-flex justify-content-center align-items-center">
          <input
            type="checkbox"
            className="form-check-input"
            checked={line.is_mines}
            onChange={() => {
              const newIsMines = !line.is_mines;
              updateField(line.rowIndex, "is_mines", newIsMines, setCsvData, setValidatedData);
              if (newIsMines && line.is_ece) {
                updateField(line.rowIndex, "is_ece", false, setCsvData, setValidatedData);
              }
            }}
          />
        </div>
      ),
    },
    {
      name: translate(language, "PAGES.DIPLOMA_IMPORT.TABLE_HEADER.MAIL_IN_ENGLISH"),
      key: "in_english",
      render: (line: DiplomaImportStructuredData) => (
        <div className="form-check d-flex justify-content-center align-items-center">
          <input
            type="checkbox"
            className="form-check-input"
            checked={line.in_english}
            onChange={() => updateField(line.rowIndex, "in_english", !line.in_english, setCsvData, setValidatedData)}
          />
        </div>
      ),
    },
    {
      name: translate(language, "PAGES.DIPLOMA_IMPORT.TABLE_HEADER.B2B"),
      key: "is_b2b",
      render: (line: DiplomaImportStructuredData) => (
        <div className="form-check d-flex justify-content-center align-items-center">
          <input
            type="checkbox"
            className="form-check-input"
            checked={line.is_b2b}
            onChange={() => updateField(line.rowIndex, "is_b2b", !line.is_b2b, setCsvData, setValidatedData)}
          />
        </div>
      ),
    },
    {
      name: translate(language, "PAGES.DIPLOMA_IMPORT.TABLE_HEADER.ECE"),
      key: "is_ece",
      render: (line: DiplomaImportStructuredData) => (
        <div className="form-check d-flex justify-content-center align-items-center">
          <input
            type="checkbox"
            className="form-check-input"
            checked={line.is_ece}
            onChange={() => {
              const newIsEce = !line.is_ece;
              updateField(line.rowIndex, "is_ece", newIsEce, setCsvData, setValidatedData);
              if (newIsEce && line.is_mines) {
                updateField(line.rowIndex, "is_mines", false, setCsvData, setValidatedData);
              }
            }}
          />
        </div>
      ),
    },
    {
      name: translate(language, "PAGES.DIPLOMA_IMPORT.TABLE_HEADER.IS_PUBLIC"),
      key: "is_public",
      render: (line: DiplomaImportStructuredData) => (
        <div className="form-check d-flex justify-content-center align-items-center">
          <input
            type="checkbox"
            className="form-check-input"
            checked={line.is_public}
            onChange={() => updateField(line.rowIndex, "is_public", !line.is_public, setCsvData, setValidatedData)}
          />
        </div>
      ),
    },
    {
      name: translate(language, "PAGES.DIPLOMA_IMPORT.TABLE_HEADER.SEND_EMAIL"),
      key: "send_email",
      render: (line: DiplomaImportStructuredData) => (
        <div className="form-check d-flex justify-content-center align-items-center">
          <input
            type="checkbox"
            className="form-check-input"
            checked={line.send_email}
            onChange={() => updateField(line.rowIndex, "send_email", !line.send_email, setCsvData, setValidatedData)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="p-3">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex">
              <span
                className="dst-icon-chevron-left p-2 medium-gray-background rounded me-2"
                onClick={(event: React.MouseEvent<HTMLDivElement>) => completeNavigate(event, navigate, "/diplomas")}
              />
              <h2 className="mb-0">{translate(language, "PAGES.DIPLOMA_IMPORT.TITLE")}</h2>
            </div>
            <div className="d-flex gap-2">
              {csvData.length > 0 && (
                <DstButton
                  btnImageBefore={<span className="dst-icon-upload-03 me-2" />}
                  value={translate(language, "PAGES.DIPLOMA_IMPORT.NEW_CSV_IMPORT")}
                  variant="light"
                  clickFunction={() => document.getElementById("file-input")?.click()}
                />
              )}
              <DstButton
                btnImageBefore={<span className="dst-icon-download-02 me-2" />}
                value={translate(language, "PAGES.DIPLOMA_IMPORT.DOWNLOAD_TEMPLATE")}
                variant="light"
                clickFunction={() => {
                  const link = document.createElement("a");
                  link.href = "/share/diploma_template.csv";
                  link.download = "import_diploma_template.csv";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              />
              <DstButton
                btnImageAfter={<span className="dst-icon-arrow-right ms-2" />}
                value={translate(language, "PAGES.DIPLOMA_IMPORT.CREATE_DIPLOMAS")}
                variant="primary"
                loading={submitting || validating}
                disabled={submitting || validating || validatedData.filter((user) => user.diplomable).length === 0}
                clickFunction={() => {
                  if (showAlert && popAlert) {
                    createImportedDiplomas(validatedData, language, setSubmitting, showAlert, popAlert);
                  }
                }}
              />
            </div>
          </div>
        </Col>
      </Row>
      {csvData.length === 0 ? (
        <div className="d-flex flex-column gap-2 clickable">
          <DstTable data={csvData} columns={columns} itemsPerPage={20} />
          <div
            ref={dropZoneRef}
            className="border-2 border-dashed rounded-3 p-5 text-center d-flex flex-column align-items-center"
          >
            <div className="text-center s-regular d-flex align-items-center">
              {translate(language, "PAGES.DIPLOMA_IMPORT.DROP_FILE")}
              <DstButton
                value={translate(language, "PAGES.DIPLOMA_IMPORT.LOAD_LOCAL_FILE")}
                variant="link"
                btnClass="ps-1"
                clickFunction={() => document.getElementById("file-input")?.click()}
              />
            </div>
          </div>
        </div>
      ) : (
        <React.Fragment>
          <DstTable
            data={validatedData.map((vData, index) => ({
              ...vData,
              rowIndex: index,
            }))}
            columns={columns}
            itemsPerPage={20}
            classTable="xs-bold"
            classLine={(item: DiplomaImportValidatedData) => (!item.diplomable ? "table-secondary" : "")}
          />
        </React.Fragment>
      )}
      <input
        type="file"
        id="file-input"
        className="d-none"
        onChange={(event) => processFileInput(event, _uploadCsvFile)}
      />
    </div>
  );
};

export default DiplomaImport;
